import React, { useEffect, useState, useMemo } from "react";

// Styles
import CompanyContainer from "./Company.styles";

// Components
import SubMenuTitle from "../../SubMenuTitle/SubMenuTitle";
import SubMenuItem from "../../SubMenuItem/SubMenuItem";
import Select from "components/Common/Select/Select";
import { MenuItem, Popover, Typography } from "@mui/material";
import ButtonExtraSmall from "components/Common/ButtonExtraSmall/ButtonExtraSmall";

// React Router
import { useHistory, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faClipboard,
    faChartLine,
    faExternalLinkAlt,
    faThumbsUp,
    faDollar,
    faDownload,
    faCircleCheck,
    faTable,
} from "@fortawesome/free-solid-svg-icons";

import * as Database from "utils/database.utils";
import { useBQMQ } from "context/BQMQ.context";
import { useSettings } from "context/Settings.context";
import { exportFromS3 } from "utils/export.utils";

interface CompanyLink {
    id: number;
    link_type_id: number;
    link_type_name: string;
    url: string;
}

const useCompanyLinks = (companyId: string | null) => {
    const [links, setLinks] = useState<CompanyLink[]>([]);

    useEffect(() => {
        if (!companyId) {
            setLinks([]);
            return;
        }

        Database.get(`api/company/${companyId}/links`).then(
            async (response) => {
                if (response.ok) {
                    const data = await response.json();
                    setLinks(data.data || []);
                }
            }
        );
    }, [companyId]);

    return links;
};

const extractCompanyIdFromUrl = (
    pathname: string,
    search: string
): string | null => {
    // Check path param (/company/company-summary/:company_id)
    const pathMatch = pathname.match(/\/company\/company-summary\/([^/]+)/);
    if (pathMatch) return pathMatch[1];

    // Check query param (?company_id=xxx)
    const queryParams = new URLSearchParams(search);
    const queryCompanyId = queryParams.get("company_id");
    if (queryCompanyId) return queryCompanyId;

    return null;
};

const COMPANY_STORAGE_KEY = "company.lastSelectedCompanyId";

const Company: React.FC = () => {
    const history = useHistory();
    const location = useLocation();
    const { companies, bqmqLoading } = useBQMQ();
    const { setToast } = useSettings();

    const [companyId, setCompanyId] = useState<string>("");
    const [thesisAnchor, setThesisAnchor] = useState<HTMLElement | null>(null);
    const companyLinks = useCompanyLinks(companyId);

    // Once companies load, validate the stored company ID against the list.
    // If it no longer exists, clear it from state, sessionStorage, and the URL.
    useEffect(() => {
        if (bqmqLoading || !companies.length || !companyId) return;
        const valid = companies.some(
            (c: BQMQDropdownCompany) => c.value === companyId
        );
        if (!valid) {
            setCompanyId("");
            sessionStorage.removeItem(COMPANY_STORAGE_KEY);
            if (location.pathname.startsWith("/company/voting")) {
                history.replace("/company/voting");
            } else if (location.pathname.startsWith("/company/company-summary/")) {
                history.replace("/company/company-summary");
            } else if (location.pathname.startsWith("/company/morning-meeting-company-notes")) {
                history.replace("/company/morning-meeting-notes");
            } else {
                const params = new URLSearchParams(location.search);
                params.delete("company_id");
                history.replace({ search: params.toString() });
            }
        }
    }, [bqmqLoading, companies]); // eslint-disable-line react-hooks/exhaustive-deps

    // Sync company ID with URL and sessionStorage
    useEffect(() => {
        const urlCompanyId = extractCompanyIdFromUrl(
            location.pathname,
            location.search
        );

        if (urlCompanyId) {
            // URL has company ID - use it and sync to sessionStorage
            if (urlCompanyId !== companyId) {
                setCompanyId(urlCompanyId);
                sessionStorage.setItem(COMPANY_STORAGE_KEY, urlCompanyId);
            }
        } else {
            // No company in URL - check if we should restore from sessionStorage
            const savedCompanyId = sessionStorage.getItem(COMPANY_STORAGE_KEY);

            if (savedCompanyId) {
                setCompanyId(savedCompanyId);

                // Update URL with saved company ID
                const currentPath = location.pathname;
                if (currentPath.includes("/company/company-summary")) {
                    history.replace(
                        `/company/company-summary/${savedCompanyId}`
                    );
                } else if (currentPath.startsWith("/company/")) {
                    const params = new URLSearchParams(location.search);
                    params.set("company_id", savedCompanyId);
                    history.replace(`${currentPath}?${params.toString()}`);
                }
            } else if (companyId) {
                // URL has no company and no saved company - clear state
                setCompanyId("");
            }
        }
    }, [location.pathname, location.search, history]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleCompanyChange = (value: string | number) => {
        const newCompanyId = value ? String(value) : null;
        setCompanyId(newCompanyId || "");

        // Save to sessionStorage for persistence across navigation
        if (newCompanyId) {
            sessionStorage.setItem(COMPANY_STORAGE_KEY, newCompanyId);
        } else {
            sessionStorage.removeItem(COMPANY_STORAGE_KEY);
        }

        const currentPath = location.pathname;
        let newUrl = currentPath;

        if (currentPath.includes("/company/company-summary")) {
            newUrl = newCompanyId
                ? `/company/company-summary/${newCompanyId}`
                : "/company/company-summary";
            history.push(newUrl);
            return;
        }

        if (
            currentPath.includes("/morning-meeting-notes") ||
            currentPath.includes("/morning-meeting-company-notes")
        ) {
            if (newCompanyId) {
                history.push(
                    `/company/morning-meeting-company-notes?company_id=${newCompanyId}`
                );
            } else {
                history.push("/company/morning-meeting-notes");
            }
            return;
        }

        if (currentPath.startsWith("/company/voting")) {
            if (newCompanyId) {
                history.push(
                    `/company/voting/result/company?company_id=${newCompanyId}`
                );
            } else {
                history.push("/company/voting");
            }
            return;
        }

        // For all other pages under /company/, use query param and stay on same page
        const params = new URLSearchParams(location.search);

        if (newCompanyId) {
            params.set("company_id", newCompanyId);
        } else {
            params.delete("company_id");
        }

        const queryString = params.toString();
        newUrl = queryString ? `${currentPath}?${queryString}` : currentPath;

        history.push(newUrl);
    };

    const companyOptions = useMemo(
        () =>
            companies.map((comp: BQMQDropdownCompany) => ({
                label: comp.label,
                value: String(comp.value),
            })),
        [companies]
    );

    const selectedCompany = useMemo(
        () =>
            companyId
                ? companies.find(
                      (comp: BQMQDropdownCompany) =>
                          String(comp.value) === companyId
                  ) ?? null
                : null,
        [companyId, companies]
    );

    const fvUpside =
        selectedCompany?.fair_value_upside_pct != null
            ? Math.round(selectedCompany.fair_value_upside_pct * 100)
            : null;

    const handleModelDownload = async () => {
        try {
            if (!selectedCompany?.redshift_company_id) {
                throw new Error("Company does not have a model available");
            }

            const extractionResponse = await Database.get(
                `api/extractions/extraction_id-by_redshift_id/${selectedCompany.redshift_company_id}`
            );

            if (!extractionResponse.ok) {
                throw new Error("Failed to fetch model information");
            }

            const extractionData = await extractionResponse.json();
            const extractionId = extractionData.data.extraction_id;
            const archiveFilePath = extractionData.data.archive_file_path;

            if (!extractionId || !archiveFilePath) {
                throw new Error("Model not available for this company");
            }

            // Download the model
            await exportFromS3(extractionId, archiveFilePath);
        } catch (error) {
            console.error("Error downloading model:", error);
            setToast({
                show: true,
                type: "error",
                message: "Failed to download model",
            });
        }
    };

    return (
        <CompanyContainer className="submenu">
            <div className="top">
                <h1>COMPANY</h1>
                <div className="companySelect">
                    <Select
                        filterName="Select Company"
                        value={companyId}
                        setValue={handleCompanyChange}
                        options={companyOptions}
                        loadingOptions={!!bqmqLoading}
                        searchable
                        showAll={false}
                        clearable={true}
                    />
                </div>
                <hr />
                {selectedCompany && (
                    <>
                        <div className="companyName">
                            {selectedCompany.label}
                        </div>
                        <div className="companyMetrics">
                            <div className="metric">
                                <span className="metricLabel">PTF WGT</span>
                                <span className="metricValue">
                                    {selectedCompany.portfolio_weight != null
                                        ? `${(
                                              (selectedCompany.portfolio_weight as number) *
                                              100
                                          ).toFixed(1)}%`
                                        : "-"}
                                </span>
                            </div>
                            <div className="metric">
                                <span className="metricLabel">BQ</span>
                                <span
                                    className={`metricScore score-${
                                        selectedCompany.bq ?? ""
                                    }`}
                                >
                                    {selectedCompany.bq ?? "-"}
                                </span>
                            </div>
                            <div className="metric">
                                <span className="metricLabel">FV Upside</span>
                                <span className="metricValue">
                                    {fvUpside != null ? `${fvUpside}%` : "-"}
                                </span>
                            </div>
                            <div className="metric">
                                <span className="metricLabel">MQ</span>
                                <span
                                    className={`metricScore score-${
                                        selectedCompany.mq ?? ""
                                    }`}
                                >
                                    {selectedCompany.mq ?? "-"}
                                </span>
                            </div>
                            {selectedCompany.investment_thesis && (
                                <>
                                    <ButtonExtraSmall
                                        text="INVESTMENT CASE"
                                        color="primary"
                                        click={(
                                            e: React.MouseEvent<HTMLElement>
                                        ) => setThesisAnchor(e.currentTarget)}
                                    />
                                    <Popover
                                        open={Boolean(thesisAnchor)}
                                        anchorEl={thesisAnchor}
                                        onClose={() => setThesisAnchor(null)}
                                        anchorOrigin={{
                                            vertical: "bottom",
                                            horizontal: "left",
                                        }}
                                        transformOrigin={{
                                            vertical: "top",
                                            horizontal: "left",
                                        }}
                                    >
                                        <Typography
                                            sx={{
                                                p: 2,
                                                maxWidth: 720,
                                                fontSize: 14,
                                            }}
                                        >
                                            {selectedCompany.investment_thesis}
                                        </Typography>
                                    </Popover>
                                </>
                            )}
                            {!!selectedCompany.extended_fade_flag && (
                                <div className="extendedFade">
                                    <span className="metricLabel">FADE</span>
                                    <FontAwesomeIcon
                                        icon={faCircleCheck}
                                        className="extendedFadeIcon"
                                    />
                                </div>
                            )}
                        </div>
                    </>
                )}
                <SubMenuItem
                    text={"Company summary"}
                    active={history.location.pathname.includes(
                        "/company-summary"
                    )}
                    icon={faChartLine}
                    click={() => {
                        if (companyId) {
                            history.push(
                                `/company/company-summary/${companyId}`
                            );
                        } else {
                            history.push("/company/company-summary");
                        }
                    }}
                    permissions={[
                        "P_bqmq_can_host",
                        "p_bqmq_can_view_results",
                        "p_bqmq_can_vote",
                    ]}
                />
                <SubMenuItem
                    text={"Earnings summary"}
                    active={history.location.pathname.includes(
                        "/earnings-summary"
                    )}
                    icon={faTable}
                    click={() => {
                        if (companyId) {
                            history.push(
                                `/company/earnings-summary?company_id=${companyId}`
                            );
                        } else {
                            history.push("/company/earnings-summary");
                        }
                    }}
                    permissions={[
                        "P_bqmq_can_host",
                        "p_bqmq_can_view_results",
                        "p_bqmq_can_vote",
                    ]}
                />
                <SubMenuTitle text="BQMQ">
                    <MenuItem
                        onClick={() => {
                            if (companyId) {
                                history.push(
                                    `/company/voting/sessions?company_id=${companyId}`
                                );
                            } else {
                                history.push("/company/voting/sessions");
                            }
                        }}
                    >
                        Sessions
                    </MenuItem>
                    <MenuItem
                        onClick={() => {
                            if (companyId) {
                                history.push(
                                    `/company/voting/create-session?company_id=${companyId}`
                                );
                            } else {
                                history.push("/company/voting/create-session");
                            }
                        }}
                    >
                        Create New Session
                    </MenuItem>
                </SubMenuTitle>
                <SubMenuItem
                    text={"BQMQ Home"}
                    active={
                        (history.location.pathname === "/company/voting" ||
                            history.location.pathname.includes(
                                "/company/voting/result"
                            )) &&
                        !history.location.pathname.includes("sessions") &&
                        !history.location.pathname.includes("create-session")
                    }
                    icon={faThumbsUp}
                    click={() => {
                        if (companyId) {
                            history.push(
                                `/company/voting/result/company?company_id=${companyId}`
                            );
                        } else {
                            history.push("/company/voting");
                        }
                    }}
                    permissions={["p_bqmq_can_vote", "p_bqmq_can_view_results"]}
                />
                <SubMenuTitle text="MODEL REVIEW" />
                <SubMenuItem
                    text={"Fair Value Charts"}
                    active={
                        history.location.pathname ===
                        "/company/fair-value-charts"
                    }
                    icon={faDollar}
                    click={() => {
                        if (companyId) {
                            history.push(
                                `/company/fair-value-charts?company_id=${companyId}`
                            );
                        } else {
                            history.push("/company/fair-value-charts");
                        }
                    }}
                    permissions={["p_bqmq_can_view_results"]}
                />
                <SubMenuItem
                    text={"Financials"}
                    active={history.location.pathname === "/company/financials"}
                    icon={faDollar}
                    click={() => {
                        if (companyId) {
                            history.push(
                                `/company/financials?company_id=${companyId}`
                            );
                        } else {
                            history.push("/company/financials");
                        }
                    }}
                    permissions={["p_bqmq_can_view_results"]}
                />
                <SubMenuItem
                    text={"Model Assumptions"}
                    active={
                        history.location.pathname ===
                        "/company/nopat_assumptions"
                    }
                    icon={faDollar}
                    click={() => {
                        if (companyId) {
                            history.push(
                                `/company/nopat_assumptions?company_id=${companyId}`
                            );
                        } else {
                            history.push("/company/nopat_assumptions");
                        }
                    }}
                    permissions={["p_bqmq_can_view_results"]}
                />
                {companyId && (
                    <SubMenuItem
                        text={"Model Download"}
                        active={false}
                        icon={faDownload}
                        click={handleModelDownload}
                        permissions={["p_bqmq_can_view_results"]}
                    />
                )}

                {companyLinks.length > 0 && (
                    <>
                        <SubMenuTitle text="EXTERNAL" />
                        {[...companyLinks]
                            .sort((a, b) => a.link_type_id - b.link_type_id)
                            .map((link) => (
                                <SubMenuItem
                                    key={link.id}
                                    text={link.link_type_name}
                                    active={false}
                                    icon={faExternalLinkAlt}
                                    click={() =>
                                        window.open(link.url, "_blank")
                                    }
                                    permissions={[]}
                                />
                            ))}
                    </>
                )}

                <SubMenuTitle text="MORNING MEETING NOTES">
                    <MenuItem
                        onClick={() => {
                            history.push("/company/new-notes");
                        }}
                    >
                        New notes
                    </MenuItem>
                    <MenuItem
                        onClick={() => {
                            history.push("/company/other-notes");
                        }}
                    >
                        Other notes
                    </MenuItem>
                </SubMenuTitle>
                <SubMenuItem
                    text={"Company's MM Notes"}
                    active={
                        history.location.pathname.includes(
                            "/morning-meeting-notes"
                        ) ||
                        history.location.pathname.includes(
                            "/morning-meeting-company-notes"
                        ) ||
                        history.location.pathname.includes(
                            "/morning-meeting-topic-classifier-notes"
                        )
                    }
                    icon={faClipboard}
                    click={() => {
                        if (companyId) {
                            history.push(
                                `/company/morning-meeting-company-notes?company_id=${companyId}`
                            );
                        } else {
                            history.push("/company/morning-meeting-notes");
                        }
                    }}
                    permissions={[
                        "P_bqmq_can_host",
                        "p_bqmq_can_view_results",
                        "p_bqmq_can_vote",
                    ]}
                />
            </div>
        </CompanyContainer>
    );
};
export default Company;
